import type { Cell, Workbook, Worksheet } from 'exceljs';
import { detectTextDocument, prefersText, TEXT_FORMATS, type TextFormat } from '../formats/text-format';
import type { DocumentFormat, DocumentProgressEvent, DocumentSource, ViewerMemoryStats, ViewerOptions } from '../models/viewer-types';
import { createDownloadAction } from '../utils/download-original';
import { formatFileSize } from '../utils/file-size';
import { createMediaViewer, detectMedia, type DetectedMedia, type MediaInput, type MediaViewer } from './media-viewer';
import { createTextViewer, type TextViewer } from './text-viewer';
import { chooseUnknownFormat, unknownExtension } from './unknown-format';
import { setToolbarIcon } from './viewer-icons';

/** What a document can be opened from: bytes in hand, a link, or a source that reads itself. */
type OpenInput = ArrayBuffer | string | DocumentSource;

/** A merged cell range, in sheet coordinates. */
interface MergeRange {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/** One spreadsheet match: its row and column. */
interface SheetHit {
  r: number;
  c: number;
}

export interface Viewer {
  openUrl(value: string, fileName?: string, format?: DocumentFormat, language?: string, automatic?: boolean): Promise<void>;
  openFile(file: File | Blob, fileName?: string, format?: DocumentFormat, language?: string, automatic?: boolean): Promise<void>;
  openSource(source: DocumentSource): Promise<void>;
  clear(options?: { preserveTextPreferences?: boolean }): void;
  setUnknownFileBehavior(value: ViewerOptions['unknownFileBehavior']): void;
  setContentsVisible(value: boolean): void;
  setView(value: ViewerOptions['view']): void;
  setWordWrap(value: boolean): void;
  setSizeUnits(value: string): void;
  memoryStats(): ViewerMemoryStats | null;
  destroy(): void;
}

const DEFAULT_TIMEOUT_MS = 60000;
const MAX_ROWS = 100000;
const MAX_COLS = 500;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const EOCD_SIGNATURE = 0x06054b50;
const CATALOG_SIGNATURE = 0x02014b50;

const LABEL_ATTRIBUTE = 'aria-label';
const SEARCH_ID = 'search';
const NEXT_ID = 'next';
const MATCHES_ID = 'matches';
const META_ID = 'meta';
const TABS_ID = 'tabs';
const SELECTION_ID = 'selection';
const READ_ONLY_TEXT = 'Read-only · select and copy text';
const COLINDEX_ATTRIBUTE = 'aria-colindex';
const SEARCH_TOOLS_ID = 'search-tools';
const DOCX_FIT_ID = 'docx-fit';
const ROLE_ATTRIBUTE = 'role';
const SRCDOC_ATTRIBUTE = 'srcdoc';
const SEARCH_TEXT_PLACEHOLDER = 'Search document text';
const PDF_MIME = 'application/pdf';
const SIZE_LIMIT_MESSAGE = 'The file exceeds the configured size limit.';

const progressNames: Record<DocumentProgressEvent['phase'], string> = {
  idle: '',
  downloading: 'Downloading',
  reading: 'Reading document',
  rendering: 'Rendering preview',
  ready: 'Ready',
  error: ''
};

/**
 * A cell's value as the reader should see it: a formula by its cached result, rich text flattened.
 *
 * @param {unknown} value The value as the workbook stores it.
 * @return {string} The text to show.
 */
function cellText(value: unknown): string {
  if (value == null) return '';

  if (value instanceof Date) return value.toLocaleDateString('en-US');

  if (typeof value !== 'object') return String(value);

  const record = value as Record<string, unknown>;

  // Formulas are never recalculated here, so a cell without a cached result has nothing to show.
  if ('formula' in record || 'sharedFormula' in record) {
    return record.result == null ? '⚠ No cached result' : cellText(record.result);
  }

  if (record.richText) return (record.richText as Array<{ text: string }>).map((part) => part.text).join('');

  if (record.error) return String(record.error);

  return record.text == null ? '' : String(record.text);
}

/**
 * A cell as it is displayed, with its own number format applied where that format is about numbers
 * rather than dates.
 *
 * @param {Cell} cell The cell to render.
 * @return {string} The text to show.
 */
function display(cell: Cell): string {
  const value = cell.value as unknown;
  const record = value as Record<string, unknown>;
  const resolved = value && typeof value === 'object' && ('formula' in record || 'sharedFormula' in record) ? record.result : value;

  if (typeof resolved === 'number' && cell.numFmt && cell.numFmt !== 'General') {
    const format = cell.numFmt.split(';')[0];

    if (/[0#]/.test(format) && !/[dmyhs]/i.test(format.replace(/"[^"]*"/g, ''))) {
      const decimals = (/\.([0#]+)/.exec(format)?.[1] || '').length;
      const percent = format.includes('%');

      return (
        (resolved * (percent ? 100 : 1)).toLocaleString('en-US', {
          minimumFractionDigits: Math.min(decimals, 10),
          maximumFractionDigits: Math.min(decimals, 10),
          useGrouping: format.includes(',')
        }) + (percent ? '%' : '')
      );
    }
  }

  return cellText(value);
}

function color(value?: { argb?: string } | null): string | null {
  return value?.argb && /^[0-9a-f]{8}$/i.test(value.argb) ? '#' + value.argb.slice(2) : null;
}

// The last index whose offset is still at or before the given position.
function position(offsets: Array<number>, value: number): number {
  let low = 0;
  let high = offsets.length - 1;

  while (low < high) {
    const middle = (low + high + 1) >> 1;

    if (offsets[middle] <= value) low = middle;
    else high = middle - 1;
  }

  return low;
}

// The ranges a worksheet lists as merged, as numbers rather than `A1:B2` strings.
function parseMerges(ranges: Array<string>): Array<MergeRange> {
  const parse = (address: string): { r: number; c: number } => {
    const [, letters, digits] = /^([A-Z]+)(\d+)$/.exec(address) as RegExpExecArray;

    return {
      r: +digits,
      c: [...letters].reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0)
    };
  };

  return ranges.map((range) => {
    const [first, second = first] = range.split(':');
    const from = parse(first);
    const to = parse(second);

    return { top: from.r, left: from.c, bottom: to.r, right: to.c };
  });
}

/**
 * The page a rendered Word document is written into. It is served with a policy that allows only the
 * images and fonts the renderer inlined, so the document cannot reach anywhere on its own.
 *
 * @param {string} styles Stylesheet markup the renderer produced.
 * @param {string} body Document markup the renderer produced.
 * @return {string} The page to put in the frame.
 */
function docxPage(styles: string, body: string): string {
  const css =
    'html,body{margin:0;background:#f7f9fc}body{width:max-content;min-width:100%}' +
    '.docx-wrapper{padding:24px!important;background:transparent!important}' +
    'section.docx{box-shadow:0 2px 12px #20314a18!important}' +
    'mark.viewer-match{background:#ffdf83;color:inherit}' +
    'mark.viewer-match.current{background:#ffa94d;outline:2px solid #b56b00}';

  return (
    '<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" ' +
    `content="default-src 'none'; img-src data: blob:; font-src data: blob:; style-src 'unsafe-inline'; ` +
    `base-uri 'none'; form-action 'none';"><style>${css}</style>` +
    styles +
    '</head><body>' +
    body +
    '</body></html>'
  );
}

/**
 * An isolated renderer owned by one component instance: it opens one document at a time, and every
 * listener, timer, frame and worker it makes along the way is released when it is destroyed.
 *
 * @param {HTMLElement} root The viewer's own markup, which this drives.
 * @param {ViewerOptions} [options] Limits, formats, and where to report.
 * @return {Viewer} The ways to open a document, and to put the viewer away.
 */
export function createViewer(root: HTMLElement, options: ViewerOptions = {}): Viewer {
  const document = root.ownerDocument;
  const $ = <T extends HTMLElement = HTMLElement>(id: string): T => root.querySelector('#' + id) as T;
  const listeners: Array<() => void> = [];
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const pendingFrames = new Map<HTMLIFrameElement, () => void>();
  const pendingYields = new Set<() => void>();

  let destroyed = false;

  const listen = (target: EventTarget, event: string, handler: EventListener): void => {
    target.addEventListener(event, handler);
    listeners.push(() => target.removeEventListener(event, handler));
  };
  const schedule = (handler: () => void, delay: number): ReturnType<typeof setTimeout> => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      handler();
    }, delay);

    timers.add(timer);

    return timer;
  };
  const isCurrent = (id: number): boolean => !destroyed && id === requestId;
  const notifyError = (error: Error): void =>
    options.onError?.({ code: 'load_failed', message: error.message || 'Unable to open document.' });

  const viewport = $('viewport');
  const canvas = $('canvas');

  let docxFrame = $<HTMLIFrameElement>('docx-frame');
  let docxScale = 1;
  let docxFit = false;
  let docxFitPage = false;
  let stopDocxTracking = (): void => undefined;
  let updateDocxPage = (): void => undefined;
  let mediaViewer: MediaViewer | null = null;
  let textViewer: TextViewer | null = null;
  let textWrap = Boolean(options.wordWrap);
  let automaticFormat = true;

  const formatError = (): Error =>
    Error(automaticFormat ? 'Unable to determine the file format.' : 'The selected format is not suitable for this file.');

  const textRoot = $('text-preview');
  const mediaRoot = $('media-preview');

  let currentFileSize = 0;
  let metadata = (): string => '';

  const sizeLabel = (bytes: number): string => formatFileSize(bytes, options.sizeUnits);

  function setMetadata(render: () => string): void {
    metadata = render;
    $(META_ID).textContent = render();
  }

  let mode: string | null = null;
  let docxHits: Array<HTMLElement> = [];
  let docxHitIndex = -1;

  const MAX_BYTES = options.maxFileBytes ?? 30 * 1024 * 1024;
  const MAX_PDF_BYTES = options.maxPdfBytes ?? 100 * 1024 * 1024;

  let workbook: Workbook | null = null;
  let sheet: Worksheet | null = null;
  let originalName = '';
  let rows = 0;
  let cols = 0;
  let xs: Array<number> = [];
  let ys: Array<number> = [];
  let merges: Array<MergeRange> = [];
  let selected: string | null = null;
  let hits: Array<SheetHit> = [];
  let hitIndex = -1;
  let requestId = 0;
  let controller: AbortController | undefined;

  const status = (message: string, error = false): void => {
    if (destroyed) return;

    $('status').textContent = message;
    $('status').className = error ? 'error' : '';
  };

  setToolbarIcon($('download'), 'download');

  const downloadAction = createDownloadAction($<HTMLButtonElement>('download'), {
    getLimit: () => (mode === 'pdf' ? MAX_PDF_BYTES : MAX_BYTES),
    onError: (message: string) => status(message, true),
    timeoutMs: options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  });

  let lastProgress: DocumentProgressEvent | null = null;
  let lastProgressTime = 0;
  let fileReader: FileReader | null = null;

  function progress(phase: DocumentProgressEvent['phase'], percent: number | null = null, loadedBytes?: number, totalBytes?: number): void {
    if (destroyed) return;

    if (phase === 'ready') downloadAction.ready();

    const now = performance.now();

    // Download progress arrives far faster than it is worth repainting.
    if (phase === 'downloading' && lastProgress?.phase === phase && lastProgress.percent === percent && now - lastProgressTime < 100) {
      return;
    }

    lastProgress = { phase, percent, loadedBytes, totalBytes };
    lastProgressTime = now;

    const region = $('progress-region');
    const bar = $<HTMLProgressElement>('progress-bar');

    region.hidden = ['idle', 'ready', 'error'].includes(phase);
    viewport.setAttribute('aria-busy', String(['downloading', 'reading', 'rendering'].includes(phase)));

    if (percent == null) bar.removeAttribute('value');
    else bar.value = percent;

    const bytes =
      loadedBytes == null ? '' : totalBytes ? ` · ${sizeLabel(loadedBytes)} / ${sizeLabel(totalBytes)}` : ` · ${sizeLabel(loadedBytes)}`;

    $('progress-label').textContent = progressNames[phase] + (percent == null ? '…' : ` · ${percent}%`) + bytes;
    options.onProgress?.(lastProgress);
  }

  // Yield once so the browser can paint the current phase before synchronous parsing.
  const yieldToBrowser = (): Promise<void> =>
    new Promise<void>((resolve) => {
      const finish = (): void => {
        pendingYields.delete(finish);
        resolve();
      };

      pendingYields.add(finish);
      schedule(finish, 0);
    });

  function block(className: string, x: number, y: number, width: number, height: number, value: string): HTMLDivElement {
    const element = document.createElement('div');

    element.className = className;
    Object.assign(element.style, {
      left: x + 'px',
      top: y + 'px',
      width: width + 'px',
      height: height + 'px'
    });
    element.textContent = value;

    return element;
  }

  function styleCell(element: HTMLDivElement, cell: Cell): void {
    const font = cell.font || {};
    const align = cell.alignment || {};

    if (font.bold) element.style.fontWeight = '700';

    if (font.italic) element.style.fontStyle = 'italic';

    if (font.underline) element.style.textDecoration = 'underline';

    if (font.size) element.style.fontSize = (Math.min(40, font.size) * 96) / 72 + 'px';

    if (color(font.color)) element.style.color = color(font.color) as string;

    if (color(cell.fill?.type === 'pattern' ? cell.fill.fgColor : null)) {
      element.style.backgroundColor = color((cell.fill as { fgColor?: { argb?: string } }).fgColor) as string;
    }

    if (['left', 'center', 'right'].includes(align.horizontal as string)) {
      element.style.textAlign = align.horizontal as string;
    } else if (typeof cell.value === 'number' || typeof (cell.value as { result?: unknown })?.result === 'number') {
      element.style.textAlign = 'right';
    }

    if (align.wrapText) element.style.whiteSpace = 'pre-wrap';

    for (const edge of ['Top', 'Bottom', 'Left', 'Right'] as const) {
      const border = cell.border?.[edge.toLowerCase() as 'top' | 'bottom' | 'left' | 'right'];

      if (border?.style) {
        element.style[`border${edge}` as 'borderTop'] = '1px solid ' + (color(border.color) || '#66758b');
      }
    }
  }

  function paint(): void {
    if (!sheet || mode !== 'xlsx') return;

    const grid = sheet;
    const left = viewport.scrollLeft;
    const top = viewport.scrollTop;
    const c1 = Math.max(1, position(xs, left) - 1);
    const c2 = Math.min(cols, position(xs, left + viewport.clientWidth) + 1);
    const r1 = Math.max(1, position(ys, top) - 1);
    const r2 = Math.min(rows, position(ys, top + viewport.clientHeight) + 1);
    const fragment = document.createDocumentFragment();
    const drawn = new Set<string>();
    const rowNodes = new Map<number, HTMLDivElement>();
    const rowFor = (r: number): HTMLDivElement => {
      let row = rowNodes.get(r);

      if (!row) {
        row = document.createElement('div');
        row.style.display = 'contents';
        row.setAttribute(ROLE_ATTRIBUTE, 'row');
        row.setAttribute('aria-rowindex', String(r + 1));
        rowNodes.set(r, row);
        fragment.append(row);
      }

      return row;
    };

    function draw(r: number, c: number): void {
      const key = r + ':' + c;

      if (drawn.has(key)) return;

      drawn.add(key);

      if (grid.getRow(r).hidden || grid.getColumn(c).hidden) return;

      const cell = grid.findCell(r, c) as Cell | undefined;

      if (!cell) return;

      if (cell.isMerged && cell.master.address !== cell.address) return;

      const merge = merges.find((range) => range.top === r && range.left === c);
      const endCol = Math.min(cols, merge?.right || c);
      const endRow = Math.min(rows, merge?.bottom || r);
      const shown = display(cell);
      const element = block('cell', xs[c], ys[r], xs[endCol + 1] - xs[c], ys[endRow + 1] - ys[r], shown);

      element.setAttribute(ROLE_ATTRIBUTE, 'gridcell');
      element.setAttribute(COLINDEX_ATTRIBUTE, String(c + 1));
      element.setAttribute(LABEL_ATTRIBUTE, cell.address + ': ' + shown);
      element.dataset.address = cell.address;
      element.title = shown;
      styleCell(element, cell);

      if (merge) element.style.zIndex = '2';

      if (selected === cell.address) element.classList.add('selected');

      const query = $<HTMLInputElement>(SEARCH_ID).value;

      if (query && shown.toLocaleLowerCase().includes(query.toLocaleLowerCase())) element.classList.add('hit');

      rowFor(r).append(element);
    }

    // Merged ranges are drawn from their anchor, which may well sit above or left of the viewport.
    for (const merge of merges) {
      if (merge.bottom >= r1 && merge.top <= r2 && merge.right >= c1 && merge.left <= c2) draw(merge.top, merge.left);
    }

    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) draw(r, c);

    const headerRow = document.createElement('div');

    headerRow.style.display = 'contents';
    headerRow.setAttribute(ROLE_ATTRIBUTE, 'row');
    headerRow.setAttribute('aria-rowindex', '1');

    for (let c = c1; c <= c2; c++) {
      const column = grid.getColumn(c);

      if (!column.hidden) {
        const head = block('colhead', xs[c], top, xs[c + 1] - xs[c], 28, column.letter);

        head.setAttribute(ROLE_ATTRIBUTE, 'columnheader');
        head.setAttribute(COLINDEX_ATTRIBUTE, String(c + 1));
        headerRow.append(head);
      }
    }

    for (let r = r1; r <= r2; r++) {
      if (!grid.getRow(r).hidden) {
        const head = block('rowhead', left, ys[r], 48, ys[r + 1] - ys[r], String(r));

        head.setAttribute(ROLE_ATTRIBUTE, 'rowheader');
        head.setAttribute(COLINDEX_ATTRIBUTE, '1');
        rowFor(r).append(head);
      }
    }

    const corner = block('colhead', left, top, 48, 28, '');

    corner.setAttribute(ROLE_ATTRIBUTE, 'columnheader');
    headerRow.prepend(corner);
    fragment.prepend(headerRow);
    canvas.setAttribute(ROLE_ATTRIBUTE, 'grid');
    canvas.setAttribute(LABEL_ATTRIBUTE, 'Worksheet ' + grid.name);
    canvas.setAttribute('aria-rowcount', String(rows + 1));
    canvas.setAttribute('aria-colcount', String(cols + 1));
    canvas.replaceChildren(fragment);
  }

  let paintFrame = 0;

  listen(viewport, 'scroll', () => {
    cancelAnimationFrame(paintFrame);
    paintFrame = requestAnimationFrame(paint);
  });

  const observer = new ResizeObserver(() => {
    paint();

    if (mode === 'docx' && docxFit) applyDocxZoom();
  });

  observer.observe(viewport);

  function openSheet(id: number): void {
    const grid = (workbook as Workbook).getWorksheet(id) as Worksheet;

    sheet = grid;
    selected = null;
    hits = [];
    hitIndex = -1;
    $<HTMLInputElement>(SEARCH_ID).value = '';
    $<HTMLButtonElement>(NEXT_ID).disabled = true;
    $(MATCHES_ID).textContent = '';
    rows = Math.min(MAX_ROWS, Math.max(grid.rowCount, 30));
    cols = Math.min(MAX_COLS, Math.max(grid.columnCount, 12));
    merges = parseMerges((grid.model as { merges?: Array<string> }).merges || []);
    xs = [0, 48];
    ys = [0, 28];

    for (let c = 1; c <= cols; c++) {
      xs[c + 1] = xs[c] + (grid.getColumn(c).hidden ? 0 : Math.min(600, Math.max(35, (grid.getColumn(c).width || 12) * 7 + 5)));
    }

    for (let r = 1; r <= rows; r++) {
      ys[r + 1] = ys[r] + (grid.getRow(r).hidden ? 0 : Math.min(400, Math.max(24, ((grid.getRow(r).height || 18) * 96) / 72)));
    }

    canvas.style.width = xs[cols + 1] + 'px';
    canvas.style.height = ys[rows + 1] + 'px';
    viewport.scrollTo(0, 0);
    $(TABS_ID)
      .querySelectorAll('button')
      .forEach((tab) => {
        const current = +(tab.dataset.id as string) === id;

        tab.classList.toggle('active', current);
        tab.setAttribute('aria-current', String(current));
      });
    setMetadata(
      () =>
        `XLSX · ${sizeLabel(currentFileSize)} · ${grid.name} · ` +
        `${grid.rowCount.toLocaleString('en-US')} rows · ${grid.columnCount} columns`
    );
    $(SELECTION_ID).textContent = 'Select a cell · Ctrl/Cmd+C to copy';
    status(
      'Read-only. Formulas are not recalculated; charts and images are not displayed.' +
        (grid.rowCount > MAX_ROWS || grid.columnCount > MAX_COLS ? ` Showing the first ${MAX_ROWS} rows and ${MAX_COLS} columns.` : '')
    );
    paint();
  }

  function applyDocxZoom(): void {
    if (mode !== 'docx' || !docxFrame.contentDocument?.body) return;

    const doc = docxFrame.contentDocument;

    if (docxFit) {
      doc.documentElement.style.zoom = '1';
      docxScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, docxFrame.clientWidth / Math.max(1, doc.body.scrollWidth)));
    }

    if (docxFit && docxFitPage) {
      const page = doc.querySelector('section.docx');

      if (page) {
        docxScale = Math.max(MIN_ZOOM, Math.min(docxScale, docxFrame.clientHeight / (page.getBoundingClientRect().height + 48)));
      }
    }

    doc.documentElement.style.zoom = String(docxScale);
    $('docx-zoom-label').textContent = `${Math.round(docxScale * 100)}%`;
    $<HTMLButtonElement>('docx-zoom-out').disabled = docxScale <= MIN_ZOOM;
    $<HTMLButtonElement>('docx-zoom-in').disabled = docxScale >= MAX_ZOOM;

    const fitAction = docxFit && !docxFitPage ? 'Fit page' : 'Fit width';

    $(DOCX_FIT_ID).setAttribute(LABEL_ATTRIBUTE, fitAction);
    $(DOCX_FIT_ID).title = fitAction;
    setToolbarIcon($(DOCX_FIT_ID), docxFit && !docxFitPage ? 'page' : 'width');
    updateDocxPage();
  }

  listen($('docx-zoom-out'), 'click', () => {
    docxFit = false;
    docxScale = Math.max(MIN_ZOOM, docxScale / 1.25);
    applyDocxZoom();
  });
  listen($('docx-zoom-in'), 'click', () => {
    docxFit = false;
    docxScale = Math.min(MAX_ZOOM, docxScale * 1.25);
    applyDocxZoom();
  });
  setToolbarIcon($(DOCX_FIT_ID), 'width');
  listen($(DOCX_FIT_ID), 'click', () => {
    docxFitPage = docxFit && !docxFitPage;
    docxFit = true;

    const action = docxFitPage ? 'Fit width' : 'Fit page';

    $(DOCX_FIT_ID).setAttribute(LABEL_ATTRIBUTE, action);
    $(DOCX_FIT_ID).title = action;
    setToolbarIcon($(DOCX_FIT_ID), docxFitPage ? 'width' : 'page');
    applyDocxZoom();
  });
  listen($('docx-actual'), 'click', () => {
    docxFit = false;
    docxScale = 1;
    applyDocxZoom();
  });

  function resetSearch(): void {
    $<HTMLInputElement>(SEARCH_ID).value = '';
    $<HTMLButtonElement>(NEXT_ID).disabled = true;
    $(MATCHES_ID).textContent = '';
    hits = [];
    hitIndex = -1;
    docxHits = [];
    docxHitIndex = -1;
  }

  function setDocumentName(name: string): void {
    originalName = name;
    $('filename').textContent = name;
    $('empty').hidden = true;
    $<HTMLInputElement>(SEARCH_ID).disabled = false;
    $<HTMLButtonElement>(NEXT_ID).disabled = true;
    $(SEARCH_TOOLS_ID).hidden = false;
    resetSearch();
  }

  async function showText(specification: ReturnType<typeof detectTextDocument>, name: string, id: number, fileSize: number): Promise<void> {
    const document = specification as NonNullable<typeof specification>;

    progress('rendering');
    sheet = null;
    workbook = null;
    selected = null;
    mode = document.type;
    canvas.hidden = true;
    docxFrame.hidden = true;
    docxFrame.removeAttribute(SRCDOC_ATTRIBUTE);
    textRoot.hidden = false;
    $(TABS_ID).replaceChildren();
    setDocumentName(name);

    const currentText = await createTextViewer(textRoot, document, {
      fileName: name,
      wrapLines: textWrap,
      view: options.view,
      onViewChange: (view: string) => {
        if (isCurrent(id)) {
          options.onViewChange?.({ fileName: name, documentType: document.type, view: view as never });
        }
      },
      onWrapChange: (value: boolean) => {
        if (isCurrent(id)) {
          textWrap = value;
          options.onWordWrapChange?.({ fileName: name, documentType: document.type, wordWrap: value });
        }
      },
      sizeUnits: options.sizeUnits,
      controlsHost: $('preview-controls'),
      isCurrent: () => isCurrent(id),
      getQuery: () => $<HTMLInputElement>(SEARCH_ID).value,
      onMatches: (value: string) => {
        if (isCurrent(id)) $(MATCHES_ID).textContent = value;
      }
    });

    if (!isCurrent(id)) {
      currentText?.destroy();

      return;
    }

    textViewer = currentText;
    textViewer?.setWordWrap(textWrap);
    textViewer?.setView(options.view || 'auto');
    await currentText?.ready;

    if (!isCurrent(id)) return;

    $<HTMLInputElement>(SEARCH_ID).placeholder = SEARCH_TEXT_PLACEHOLDER;
    setMetadata(() => `${document.type.toUpperCase()} · ${sizeLabel(fileSize)} · ${document.encoding.toUpperCase()}`);
    $(SELECTION_ID).textContent = READ_ONLY_TEXT;
    status('');
    progress('ready', 100);
    options.onLoaded?.({ type: document.type, fileName: name, size: fileSize, language: document.language });
  }

  async function showMedia(buffer: OpenInput, media: DetectedMedia, name: string, id: number, fileSize: number): Promise<void> {
    progress('rendering');
    canvas.hidden = true;
    docxFrame.hidden = true;
    docxFrame.removeAttribute(SRCDOC_ATTRIBUTE);
    sheet = null;
    workbook = null;
    selected = null;
    mode = media.type;
    resetSearch();
    mediaRoot.hidden = false;
    $(TABS_ID).replaceChildren();
    $('empty').hidden = true;

    const currentMedia = createMediaViewer(mediaRoot, {
      contentsVisible: options.contentsVisible,
      onContentsVisibleChange: (visible: boolean) => {
        if (isCurrent(id)) options.onContentsVisibleChange?.({ fileName: name, documentType: 'pdf', visible });
      },
      sizeUnits: options.sizeUnits,
      controlsHost: $('preview-controls'),
      onInfo: (value: string) => {
        if (isCurrent(id)) $(META_ID).textContent = value;
      },
      pdfAssetsUrl: options.pdfAssetsUrl,
      timeoutMs: options.timeoutMs,
      maxFileBytes: media.type === 'pdf' ? MAX_PDF_BYTES : MAX_BYTES,
      onProgress: (loaded: number, total?: number) => {
        if (isCurrent(id) && lastProgress?.phase !== 'ready') {
          progress('downloading', total ? Math.min(99, Math.floor((loaded / total) * 100)) : null, loaded, total);
        }
      },
      getQuery: () => $<HTMLInputElement>(SEARCH_ID).value,
      onMatches: (value: string) => {
        if (isCurrent(id)) $(MATCHES_ID).textContent = value;
      },
      onError: (error: Error) => fail(error, id)
    });

    mediaViewer = currentMedia;

    const details = await currentMedia.open(buffer as MediaInput, media, name);

    if (!isCurrent(id)) return;

    currentMedia.finish();
    setDocumentName(name);
    $(SEARCH_TOOLS_ID).hidden = media.type === 'image';
    $<HTMLInputElement>(SEARCH_ID).disabled = media.type === 'image';
    $<HTMLButtonElement>(NEXT_ID).disabled = true;
    $<HTMLInputElement>(SEARCH_ID).placeholder = media.type === 'pdf' ? 'Search PDF text' : SEARCH_TEXT_PLACEHOLDER;
    setMetadata(() =>
      media.type === 'pdf' ? `PDF · ${details?.pages} pages` : `Image · ${sizeLabel(fileSize)} · ${details?.width} × ${details?.height}`
    );

    if (media.type === 'pdf') currentMedia.updateInfo();

    $(SELECTION_ID).textContent = 'Read-only';
    status('');
    progress('ready', 100);
    options.onLoaded?.({ type: media.type, fileName: name, size: fileSize, ...details });
  }

  function trackDocxPages(id: number, fileSize: number): void {
    const doc = docxFrame.contentDocument as Document;

    doc.addEventListener('click', (event) => {
      if ((event.target as Element).closest('a')) event.preventDefault();
    });

    const pages = [...doc.querySelectorAll('section.docx')];
    const frameWindow = doc.defaultView as Window;
    let pageFrame = 0;
    const updatePage = (): void => {
      pageFrame = 0;

      if (!isCurrent(id)) return;

      // Use rendered page bounds so scrolling, search, and zoom share one position.
      let low = 0;
      let high = pages.length;

      while (low < high) {
        const middle = (low + high) >>> 1;

        if (pages[middle].getBoundingClientRect().bottom <= 1) low = middle + 1;
        else high = middle;
      }

      setMetadata(() =>
        pages.length
          ? `Word · ${sizeLabel(fileSize)} · Page ${Math.min(low + 1, pages.length)} / ${pages.length}`
          : `Word · ${sizeLabel(fileSize)}`
      );
    };

    updateDocxPage = (): void => {
      if (!pageFrame) pageFrame = requestAnimationFrame(updatePage);
    };

    const schedulePage = updateDocxPage;

    frameWindow.addEventListener('scroll', schedulePage, { passive: true });
    frameWindow.addEventListener('resize', schedulePage);

    const pageObserver = new ResizeObserver(schedulePage);

    pageObserver.observe(doc.body);
    doc.addEventListener('load', schedulePage, true);
    stopDocxTracking = (): void => {
      frameWindow.removeEventListener('scroll', schedulePage);
      frameWindow.removeEventListener('resize', schedulePage);
      doc.removeEventListener('load', schedulePage, true);
      pageObserver.disconnect();
      cancelAnimationFrame(pageFrame);
      updateDocxPage = (): void => undefined;
      stopDocxTracking = (): void => undefined;
    };
    updatePage();
  }

  async function showDocx(buffer: ArrayBuffer, name: string, id: number, fileSize: number): Promise<void> {
    status('Reading Word document…');

    const { renderAsync } = await import('./vendor/office-runtime');
    const body = document.createElement('div');
    const styles = document.createElement('div');

    progress('rendering');
    await yieldToBrowser();

    if (!isCurrent(id)) return;

    await renderAsync(buffer, body, styles, {
      inWrapper: true,
      breakPages: true,
      ignoreLastRenderedPageBreak: false,
      useBase64URL: true,
      renderAltChunks: false,
      renderComments: false
    });

    if (!isCurrent(id)) return;

    // Remove navigation targets before the untrusted document enters its sandbox.
    for (const link of body.querySelectorAll('a')) {
      link.removeAttribute('href');
      link.removeAttribute('target');
      link.setAttribute('aria-disabled', 'true');
    }

    const safeName = name || 'document.docx';
    // Each render owns its frame, so an older onload cannot overwrite a newer load.
    const nextFrame = document.createElement('iframe');

    nextFrame.title = 'Word document preview';
    nextFrame.id = 'pending-docx';
    nextFrame.hidden = true;
    nextFrame.setAttribute('sandbox', 'allow-same-origin');
    nextFrame.referrerPolicy = 'no-referrer';

    const ready = new Promise<boolean>((resolve) => {
      pendingFrames.set(nextFrame, () => {
        nextFrame.remove();
        resolve(false);
      });
      nextFrame.onload = (): void => {
        pendingFrames.delete(nextFrame);
        resolve(true);
      };
      nextFrame.srcdoc = docxPage(styles.innerHTML, body.innerHTML);
      viewport.append(nextFrame);
    });

    if (!(await ready) || !isCurrent(id)) {
      nextFrame.remove();

      return;
    }

    // Moving a live iframe would reload its browsing context and lose listeners.
    docxFrame.remove();
    docxFrame = nextFrame;
    docxFrame.id = 'docx-frame';
    mode = 'docx';
    sheet = null;
    workbook = null;
    selected = null;
    canvas.hidden = true;
    docxFrame.hidden = false;
    docxScale = 1;
    docxFit = false;
    $('docx-zoom').hidden = false;
    applyDocxZoom();
    $(TABS_ID).replaceChildren();
    setDocumentName(safeName);
    trackDocxPages(id, fileSize);
    $(SELECTION_ID).textContent = READ_ONLY_TEXT;
    viewport.scrollTo(0, 0);
    progress('ready', 100);
    options.onLoaded?.({ type: 'docx', fileName: safeName, size: fileSize });
    status('');
  }

  async function showWorkbook(buffer: ArrayBuffer, name: string, id: number, fileSize: number): Promise<void> {
    status('Reading Excel workbook…');

    const { ExcelJS } = await import('./vendor/office-runtime');

    if (!isCurrent(id)) return;

    const next = new ExcelJS.Workbook() as Workbook;

    await next.xlsx.load(buffer as never);

    if (!isCurrent(id)) return;

    const visible = next.worksheets.filter((worksheet) => worksheet.state === 'visible');

    if (!visible.length) throw Error('This workbook has no visible worksheets.');

    progress('rendering');
    await yieldToBrowser();

    if (!isCurrent(id)) return;

    mode = 'xlsx';
    workbook = next;
    docxFrame.hidden = true;
    docxFrame.removeAttribute(SRCDOC_ATTRIBUTE);
    setDocumentName(/\.xlsx$/i.test(name) ? name : name + '.xlsx');
    canvas.hidden = false;
    $(TABS_ID).replaceChildren(
      ...visible.map((worksheet) => {
        const tab = document.createElement('button');

        tab.setAttribute('part', 'viewer-sheet-tab');
        tab.textContent = worksheet.name;
        tab.dataset.id = String(worksheet.id);
        tab.onclick = (): void => openSheet(worksheet.id);

        return tab;
      })
    );
    openSheet(visible[0].id);
    progress('ready', 100);
    options.onLoaded?.({ type: 'xlsx', fileName: originalName, size: fileSize, sheets: visible.length });
  }

  async function openOfficePackage(buffer: ArrayBuffer, name: string, id: number, format: string, fileSize: number): Promise<void> {
    $<HTMLInputElement>(SEARCH_ID).placeholder = SEARCH_TEXT_PLACEHOLDER;

    if (format === 'auto' && unknownExtension(name)) {
      const signature = new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength));

      if (signature[0] !== 80 || signature[1] !== 75) throw formatError();
    }

    try {
      validateArchive(buffer, options.maxExpandedBytes);
    } catch (error) {
      if ((error as Error).message.startsWith('Invalid Office')) throw formatError();

      throw error;
    }

    // Detect the OOXML package rather than relying on a URL extension.
    const { JSZip } = await import('./vendor/office-runtime');
    let zip;

    try {
      zip = await JSZip.loadAsync(buffer);
    } catch {
      throw formatError();
    }

    if (!isCurrent(id)) return;

    if ((format === 'docx' && !zip.file('word/document.xml')) || (format === 'xlsx' && !zip.file('xl/workbook.xml'))) {
      throw formatError();
    }

    if (zip.file('word/document.xml')) return showDocx(buffer, name, id, fileSize);

    if (!zip.file('xl/workbook.xml')) throw formatError();

    return showWorkbook(buffer, name, id, fileSize);
  }

  async function load(
    buffer: OpenInput,
    name: string,
    id: number,
    contentType = '',
    format: string = options.format || 'auto',
    language: string = options.language || ''
  ): Promise<void> {
    // PDF.js transfers the input buffer to its worker, so retain its original size.
    const remotePdf = typeof buffer === 'string' || typeof (buffer as DocumentSource)?.readRange === 'function';
    const fileSize = typeof buffer === 'string' ? 0 : remotePdf ? (buffer as DocumentSource).size : (buffer as ArrayBuffer).byteLength;

    if (fileSize > Math.max(MAX_BYTES, MAX_PDF_BYTES)) throw Error(SIZE_LIMIT_MESSAGE);

    if (!isCurrent(id)) return;

    currentFileSize = fileSize;
    progress('reading');
    await yieldToBrowser();

    if (!isCurrent(id)) return;

    const media: DetectedMedia | null =
      TEXT_FORMATS.includes(format as TextFormat) || (format === 'auto' && prefersText(name))
        ? null
        : remotePdf || format === 'pdf'
          ? { type: 'pdf' }
          : ['auto', 'image'].includes(format)
            ? detectMedia(buffer as ArrayBuffer, name, contentType)
            : null;

    if (fileSize > (media?.type === 'pdf' ? MAX_PDF_BYTES : MAX_BYTES)) throw Error(SIZE_LIMIT_MESSAGE);

    const specification =
      remotePdf || media ? null : detectTextDocument(buffer as ArrayBuffer, name, contentType, format as never, language);

    if (specification) return showText(specification, name, id, fileSize);

    if (media) return showMedia(buffer, media, name, id, fileSize);

    return openOfficePackage(buffer as ArrayBuffer, name, id, format, fileSize);
  }

  function searchDocx(): void {
    const doc = docxFrame.contentDocument as Document;

    for (const mark of doc.querySelectorAll('mark.viewer-match')) {
      mark.replaceWith(doc.createTextNode(mark.textContent ?? ''));
    }

    doc.body.normalize();
    docxHits = [];
    docxHitIndex = -1;

    const query = $<HTMLInputElement>(SEARCH_ID).value.toLocaleLowerCase();

    if (query) {
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
        acceptNode: (node: Node): number =>
          node.parentElement?.closest('style,script') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
      });
      const nodes: Array<ChildNode> = [];

      while (walker.nextNode()) nodes.push(walker.currentNode as ChildNode);

      for (const node of nodes) {
        const source = node.textContent ?? '';
        const lower = source.toLocaleLowerCase();
        let at = 0;
        let index = lower.indexOf(query);

        if (index < 0) continue;

        const fragment = doc.createDocumentFragment();

        while (index >= 0) {
          fragment.append(doc.createTextNode(source.slice(at, index)));

          const mark = doc.createElement('mark');

          mark.className = 'viewer-match';
          mark.textContent = source.slice(index, index + query.length);
          fragment.append(mark);
          docxHits.push(mark);
          at = index + query.length;
          index = lower.indexOf(query, at);
        }

        fragment.append(doc.createTextNode(source.slice(at)));
        node.replaceWith(fragment);
      }
    }

    $(MATCHES_ID).textContent = query && !docxHits.length ? 'No matches' : '';

    if (docxHits.length) jumpDocx();
  }

  function jumpDocx(): void {
    if (!docxHits.length) return;

    docxHits[docxHitIndex]?.classList.remove('current');
    docxHitIndex = (docxHitIndex + 1) % docxHits.length;

    const mark = docxHits[docxHitIndex];

    mark.classList.add('current');
    mark.scrollIntoView({ block: 'center', inline: 'nearest' });
    $(MATCHES_ID).textContent = `${docxHitIndex + 1} / ${docxHits.length}`;
  }

  function closeCurrent(): void {
    downloadAction.reset();
    metadata = (): string => '';
    stopDocxTracking();
    textViewer?.destroy();
    textViewer = null;
    textRoot.hidden = true;
    mediaViewer?.destroy();
    mediaViewer = null;
    mediaRoot.hidden = true;
  }

  function fail(error: Error, id: number): void {
    if (!isCurrent(id) || error.name === 'AbortError') return;

    closeCurrent();
    progress('error');
    status(error.message || 'Unable to open the document.', true);
    notifyError(error);
  }

  function begin(): number {
    if (destroyed) throw Error('The viewer has been destroyed.');

    closeCurrent();
    $('docx-zoom').hidden = true;
    $(SEARCH_TOOLS_ID).hidden = false;
    controller?.abort();
    fileReader?.abort();
    fileReader = null;
    progress('idle');

    for (const cancel of [...pendingFrames.values()]) cancel();

    pendingFrames.clear();

    return ++requestId;
  }

  /**
   * Asks the reader what an unrecognised extension should be opened as.
   *
   * @param {string} name Name of the document.
   * @param {string} extension The extension nobody claims.
   * @param {AbortSignal} signal Cancels the question along with the request.
   * @return {Promise<string|null>} The chosen format, or null when the reader cancelled.
   */
  function askFormat(name: string, extension: string, signal: AbortSignal): Promise<string | null> {
    return chooseUnknownFormat(root, name, extension, {
      behavior: options.unknownFileBehavior,
      onSelect: options.onFormatSelected,
      signal
    });
  }

  // The bytes, or `pdf` when the first of them turned out to be a PDF and the caller should stream it.
  async function downloadBody(response: Response, id: number, name: string, format: string): Promise<ArrayBuffer | 'pdf' | null> {
    const rawLength = Number(response.headers.get('content-length'));
    const downloadLimit = format === 'auto' && !prefersText(name) ? Math.max(MAX_BYTES, MAX_PDF_BYTES) : MAX_BYTES;

    if (rawLength > downloadLimit) {
      await response.body?.cancel();

      throw Error(SIZE_LIMIT_MESSAGE);
    }

    const encoding = response.headers.get('content-encoding');
    const expected = Number.isFinite(rawLength) && rawLength > 0 && (!encoding || encoding === 'identity') ? rawLength : undefined;

    progress('downloading', expected ? 0 : null, 0, expected);

    const reader = (response.body as ReadableStream<Uint8Array>).getReader();
    const chunks: Array<Uint8Array> = [];
    let total = 0;

    for (;;) {
      const { done, value } = await reader.read();

      if (done) break;

      if (!isCurrent(id)) {
        await reader.cancel();

        return null;
      }

      // A PDF that only reveals itself in its first bytes is still opened the streaming way.
      if (
        format === 'auto' &&
        !prefersText(name) &&
        !total &&
        detectMedia(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer)?.type === 'pdf'
      ) {
        await reader.cancel();

        return 'pdf';
      }

      total += value.byteLength;

      if (total > MAX_BYTES || rawLength > MAX_BYTES) {
        await reader.cancel();

        throw Error(SIZE_LIMIT_MESSAGE);
      }

      chunks.push(value);
      progress('downloading', expected ? Math.min(99, Math.floor((total / expected) * 100)) : null, total, expected);
    }

    if (!isCurrent(id)) return null;

    progress('downloading', 100, total, total);

    const bytes = new Uint8Array(total);
    let offset = 0;

    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.length;
    }

    return bytes.buffer;
  }

  function nameFromUrl(url: URL): string {
    try {
      return decodeURIComponent(url.pathname.split('/').pop() || 'document');
    } catch {
      /* Keep a safe fallback name for malformed URL escapes. */
    }

    return 'document';
  }

  async function openUrl(
    value: string,
    fileName = '',
    format: string = options.format || 'auto',
    language: string = options.language || '',
    automatic = format === 'auto'
  ): Promise<void> {
    const id = begin();

    automaticFormat = automatic;
    textWrap = Boolean(options.wordWrap);
    controller = new AbortController();

    const activeController = controller;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let stage = 'preview';

    try {
      const url = new URL(value, document.baseURI);

      if (!['http:', 'https:'].includes(url.protocol)) throw Error('Please use an HTTP or HTTPS URL.');

      if (location.protocol === 'https:' && url.protocol === 'http:') {
        throw Error('An HTTPS page requires an HTTPS document URL.');
      }

      if (format === 'zip' || format === 'msp360-log-archive' || (format === 'auto' && /\.zip$/i.test(fileName || url.pathname))) {
        throw Error('Use ArchiveTocComponent or DocumentBrowserComponent for ZIP URLs.');
      }

      const inferredPdfName = nameFromUrl(url);
      const documentName = fileName || inferredPdfName;
      const extension = unknownExtension(documentName, {}, format as never);

      if (extension) {
        const choice = await askFormat(documentName, extension, activeController.signal);

        if (!isCurrent(id)) return;

        if (!choice) {
          clear();

          return;
        }

        format = choice;
        automaticFormat = choice === 'auto';
      }

      timeout = schedule(() => activeController.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
      downloadAction.set({ url: url.href, name: documentName });

      if (format === 'pdf' || (format === 'auto' && /\.pdf$/i.test(documentName))) {
        stage = 'preview';
        await load(url.href, documentName, id, PDF_MIME, format, language);

        return;
      }

      progress('downloading', 0);
      status('Downloading document…');
      stage = 'download';

      const response = await fetch(url, {
        signal: activeController.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });

      if (!response.ok) {
        throw Error(`The server returned HTTP ${response.status}. Check the URL and access permissions.`);
      }

      if (format === 'auto' && !prefersText(documentName) && (response.headers.get('content-type') || '').split(';')[0] === PDF_MIME) {
        await response.body?.cancel();
        stage = 'preview';
        await load(url.href, documentName, id, PDF_MIME, format, language);

        return;
      }

      const buffer = await downloadBody(response, id, documentName, format);

      if (!buffer) return;

      if (buffer === 'pdf') {
        stage = 'preview';
        await load(url.href, documentName, id, PDF_MIME, format, language);

        return;
      }

      stage = 'preview';
      await load(buffer, fileName || nameFromUrl(url), id, (response.headers.get('content-type') || '').split(';')[0], format, language);
    } catch (caught) {
      let error = caught as Error;

      if (error.name === 'TypeError') {
        error = Error(
          stage === 'download'
            ? 'Unable to download the file. Check the URL, permissions, and CORS settings.'
            : `Unable to render the document: ${error.message}`
        );
      }

      if (error.name === 'AbortError' && isCurrent(id)) error = Error('The download timed out. Please try again.');

      fail(error, id);
    } finally {
      clearTimeout(timeout);
      timers.delete(timeout as ReturnType<typeof setTimeout>);
    }
  }

  function readLocalFile(file: Blob, id: number): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();

      fileReader = reader;
      reader.onprogress = (event): void => {
        if (isCurrent(id)) {
          progress(
            'reading',
            event.lengthComputable ? Math.min(99, Math.floor((event.loaded / event.total) * 100)) : null,
            event.loaded,
            event.lengthComputable ? event.total : undefined
          );
        }
      };
      reader.onload = (): void => resolve(reader.result as ArrayBuffer);
      reader.onerror = (): void => reject(Error('Unable to read the local file.'));
      reader.onabort = (): void => reject(new DOMException('Cancelled', 'AbortError'));
      reader.readAsArrayBuffer(file);
    });
  }

  async function openFile(
    file: File | Blob,
    fileName = '',
    format: string = options.format || 'auto',
    language: string = options.language || '',
    automatic = format === 'auto'
  ): Promise<void> {
    const id = begin();

    automaticFormat = automatic;
    textWrap = Boolean(options.wordWrap);
    controller = new AbortController();

    const active = controller;

    try {
      if (!(file instanceof Blob)) throw Error('Expected a File or Blob.');

      const given = (file as File).name || '';

      if (format === 'zip' || format === 'msp360-log-archive' || (format === 'auto' && /\.zip$/i.test(fileName || given))) {
        throw Error('ZIP archive browsing is supported only for URLs.');
      }

      if (file.size > Math.max(MAX_BYTES, MAX_PDF_BYTES)) throw Error(SIZE_LIMIT_MESSAGE);

      const localName = fileName || given || 'document';
      const extension = unknownExtension(localName, {}, format as never);

      if (extension) {
        const choice = await askFormat(localName, extension, active.signal);

        if (!isCurrent(id)) return;

        if (!choice) {
          clear();

          return;
        }

        format = choice;
        automaticFormat = choice === 'auto';
      }

      downloadAction.set({ blob: file, name: localName });

      const header = await file.slice(0, 1024).arrayBuffer();

      if (!isCurrent(id)) return;

      const isPdf =
        format === 'pdf' || (format === 'auto' && !prefersText(localName) && detectMedia(header, localName, file.type)?.type === 'pdf');

      if (file.size > (isPdf ? MAX_PDF_BYTES : MAX_BYTES)) throw Error(SIZE_LIMIT_MESSAGE);

      progress('reading', 0, 0, file.size);

      const buffer = await readLocalFile(file, id);

      if (!isCurrent(id)) return;

      fileReader = null;
      progress('reading', 100, file.size, file.size);
      await load(buffer, fileName || given || 'document', id, file.type, format, language);
    } catch (error) {
      fail(error as Error, id);
    }
  }

  async function openSource(source: DocumentSource): Promise<void> {
    const id = begin();

    automaticFormat = source.automaticFormat ?? (!source.format || source.format === 'auto');
    controller = new AbortController();

    const active = controller;
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      let format: string = source.format || 'auto';
      const extension = unknownExtension(source.name, {}, format as never);

      if (extension) {
        const choice = await askFormat(source.name, extension, active.signal);

        if (!isCurrent(id)) return;

        if (!choice) {
          clear();

          return;
        }

        format = choice;
        automaticFormat = choice === 'auto';
      }

      timer = schedule(() => active.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
      downloadAction.set({ source, name: source.name });

      const pdf = format === 'pdf' || (format === 'auto' && /\.pdf$/i.test(source.name));
      const limit = pdf ? MAX_PDF_BYTES : MAX_BYTES;
      const size = source.size;

      if (!Number.isSafeInteger(size) || size < 0 || size > limit) {
        throw Error(SIZE_LIMIT_MESSAGE);
      }

      if (pdf && source.readRange) {
        await load(source, source.name, id, PDF_MIME, 'pdf', source.language || '');
      } else {
        progress('downloading');
        status('Loading document source…');

        const bytes = await source.load(active.signal);

        if (!isCurrent(id)) return;

        if (!(bytes instanceof Uint8Array) || bytes.length !== source.size) {
          throw Error('The document source returned an invalid file size.');
        }

        await load(bytes.slice().buffer, source.name, id, '', format, source.language || '');
      }
    } catch (caught) {
      let error = caught as Error;

      if (active.signal.aborted && isCurrent(id)) error = Error('The document source timed out. Please try again.');
      else if (error.name === 'AbortError' && isCurrent(id)) error = Error('The document source is no longer available.');

      fail(error, id);
    } finally {
      clearTimeout(timer);
      timers.delete(timer as ReturnType<typeof setTimeout>);
    }
  }

  function clear({ preserveTextPreferences = false } = {}): void {
    begin();

    if (!preserveTextPreferences) textWrap = Boolean(options.wordWrap);

    mode = null;
    workbook = null;
    sheet = null;
    selected = null;
    canvas.replaceChildren();
    canvas.hidden = true;
    docxFrame.hidden = true;
    docxFrame.removeAttribute(SRCDOC_ATTRIBUTE);
    $('empty').hidden = false;
    $(TABS_ID).replaceChildren();
    resetSearch();
    $('filename').textContent = 'No document open';
    $(META_ID).textContent = '';
    $(SELECTION_ID).textContent = 'Read-only';

    for (const id of [SEARCH_ID, NEXT_ID]) $<HTMLInputElement>(id).disabled = true;

    status('Pass a document URL or file to the viewer.');
  }

  canvas.onclick = (event): void => {
    const element = (event.target as Element).closest('.cell') as HTMLElement | null;

    if (!element || !sheet) return;

    selected = element.dataset.address as string;
    $(SELECTION_ID).textContent = selected + ' · ' + display(sheet.getCell(selected));
    viewport.focus();
    paint();
  };

  function moveSelection(key: string): void {
    const grid = sheet as Worksheet;
    const cell = selected ? grid.getCell(selected) : grid.getCell('A1');
    let r = Number(cell.row);
    let c = Number(cell.col);

    if (key === 'ArrowDown') r++;

    if (key === 'ArrowUp') r--;

    if (key === 'ArrowRight') c++;

    if (key === 'ArrowLeft') c--;

    r = Math.max(1, Math.min(rows, r));
    c = Math.max(1, Math.min(cols, c));
    selected = grid.getCell(r, c).address;
    $(SELECTION_ID).textContent = selected + ' · ' + display(grid.getCell(r, c));

    if (ys[r] < viewport.scrollTop + 28 || ys[r + 1] > viewport.scrollTop + viewport.clientHeight) {
      viewport.scrollTop = Math.max(0, ys[r] - 28);
    }

    if (xs[c] < viewport.scrollLeft + 48 || xs[c + 1] > viewport.scrollLeft + viewport.clientWidth) {
      viewport.scrollLeft = Math.max(0, xs[c] - 48);
    }

    paint();
  }

  function copySelection(): void {
    const value = display((sheet as Worksheet).getCell(selected as string));
    const copy = (event: Event): void => {
      (event as ClipboardEvent).clipboardData?.setData('text/plain', value);
      event.preventDefault();
    };

    document.addEventListener('copy', copy, { once: true });

    try {
      document.execCommand('copy');
    } finally {
      document.removeEventListener('copy', copy);
    }
  }

  listen(viewport, 'keydown', ((event: KeyboardEvent) => {
    if (mode === 'xlsx' && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      moveSelection(event.key);

      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c' && selected) {
      event.preventDefault();
      copySelection();
    }
  }) as EventListener);

  function jump(): void {
    if (TEXT_FORMATS.includes(mode as TextFormat)) {
      textViewer?.jump();

      return;
    }

    if (mode === 'pdf') {
      mediaViewer?.jump();

      return;
    }

    if (mode === 'docx') {
      jumpDocx();

      return;
    }

    if (!hits.length) return;

    hitIndex = (hitIndex + 1) % hits.length;

    const { r, c } = hits[hitIndex];
    const grid = sheet as Worksheet;

    selected = grid.getCell(r, c).address;
    viewport.scrollTo(Math.max(0, xs[c] - 48), Math.max(0, ys[r] - 28));
    $(MATCHES_ID).textContent = `${hitIndex + 1} / ${hits.length}`;
    $(SELECTION_ID).textContent = selected + ' · ' + display(grid.getCell(r, c));
    paint();
  }

  function searchSheet(query: string): void {
    const grid = sheet as Worksheet;

    hits = [];
    hitIndex = -1;

    if (query) {
      grid.eachRow((row, r) => {
        if (r > rows || row.hidden) return;

        row.eachCell((cell, c) => {
          if (
            c <= cols &&
            !grid.getColumn(c).hidden &&
            (!cell.isMerged || cell.master.address === cell.address) &&
            display(cell).toLocaleLowerCase().includes(query)
          ) {
            hits.push({ r, c });
          }
        });
      });
    }

    $(MATCHES_ID).textContent = query ? (hits.length ? '' : 'No matches') : '';

    if (hits.length) jump();
    else paint();
  }

  $<HTMLInputElement>(SEARCH_ID).oninput = (): void => {
    const input = $<HTMLInputElement>(SEARCH_ID);

    $<HTMLButtonElement>(NEXT_ID).disabled = !input.value.trim() || input.disabled;

    if (TEXT_FORMATS.includes(mode as TextFormat)) {
      textViewer?.search(input.value);

      return;
    }

    if (mode === 'pdf') {
      mediaViewer?.search(input.value);

      return;
    }

    if (mode === 'docx') {
      searchDocx();

      return;
    }

    if (mode !== 'xlsx') return;

    searchSheet(input.value.toLocaleLowerCase());
  };
  $<HTMLButtonElement>(NEXT_ID).onclick = jump;
  $<HTMLInputElement>(SEARCH_ID).onkeydown = (event): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      jump();
    }
  };

  return {
    openUrl: openUrl as Viewer['openUrl'],
    openFile: openFile as Viewer['openFile'],
    openSource,
    clear,
    setUnknownFileBehavior(value): void {
      options.unknownFileBehavior = value;
    },
    setContentsVisible(value: boolean): void {
      options.contentsVisible = Boolean(value);
      mediaViewer?.setContentsVisible(options.contentsVisible);
    },
    setView(value): void {
      options.view = value;
      textViewer?.setView(value as string);
    },
    setWordWrap(value: boolean): void {
      options.wordWrap = Boolean(value);
      textWrap = options.wordWrap;
      textViewer?.setWordWrap(textWrap);
    },
    setSizeUnits(value: string): void {
      options.sizeUnits = value === 'decimal' ? 'decimal' : 'binary';
      textViewer?.setSizeUnits(options.sizeUnits);

      if (mode === 'pdf') mediaViewer?.setSizeUnits(options.sizeUnits);
      else $(META_ID).textContent = metadata();

      lastProgressTime = 0;

      if (lastProgress) {
        progress(lastProgress.phase, lastProgress.percent, lastProgress.loadedBytes, lastProgress.totalBytes);
      }
    },
    memoryStats: (): ViewerMemoryStats | null => (mode === 'pdf' ? (mediaViewer?.memoryStats() ?? null) : null),
    destroy(): void {
      if (destroyed) return;

      clear();
      destroyed = true;
      downloadAction.destroy();
      observer.disconnect();
      cancelAnimationFrame(paintFrame);

      for (const remove of listeners) remove();

      for (const finish of [...pendingYields]) finish();

      for (const timer of timers) clearTimeout(timer);

      timers.clear();

      for (const id of [SEARCH_ID, NEXT_ID]) {
        const element = $<HTMLInputElement>(id);

        element.onclick = null;
        element.oninput = null;
        element.onkeydown = null;
      }

      canvas.onclick = null;
      docxFrame.onload = null;
    }
  };
}

/**
 * Reads an Office package's own ZIP catalog before anything unpacks it: a package that is encrypted,
 * split, or far larger unpacked than it looks is refused here rather than in the renderer.
 *
 * @param {ArrayBuffer} buffer The package.
 * @param {number|null} [maxExpandedBytes] The unpacked size allowed, when the host sets one.
 */
export function validateArchive(buffer: ArrayBuffer, maxExpandedBytes: number | null = null): void {
  const view = new DataView(buffer);
  let eocd = -1;

  for (let at = view.byteLength - 22; at >= Math.max(0, view.byteLength - 65557); at--) {
    if (view.getUint32(at, true) === EOCD_SIGNATURE) {
      eocd = at;
      break;
    }
  }

  if (eocd < 0) throw Error('Invalid Office ZIP package. Legacy DOC/XLS files are not supported.');

  const count = view.getUint16(eocd + 10, true);
  const offset = view.getUint32(eocd + 16, true);

  if (count === 65535 || count > 5000 || offset === 0xffffffff) {
    throw Error('This Office package is too large or uses unsupported ZIP64 metadata.');
  }

  let at = offset;
  let expanded = 0;

  for (let index = 0; index < count; index++) {
    if (at + 46 > view.byteLength || view.getUint32(at, true) !== CATALOG_SIGNATURE) {
      throw Error('Invalid Office ZIP directory.');
    }

    if (view.getUint16(at + 8, true) & 1) throw Error('Password-protected documents are not supported.');

    const size = view.getUint32(at + 24, true);

    expanded += size;

    if (size === 0xffffffff) throw Error('ZIP64 entries are not supported.');

    if (maxExpandedBytes != null && maxExpandedBytes > 0 && expanded > maxExpandedBytes) {
      throw Error('The expanded Office package exceeds the configured limit.');
    }

    at += 46 + view.getUint16(at + 28, true) + view.getUint16(at + 30, true) + view.getUint16(at + 32, true);
  }
}
